/**
 * @description Component-TradeAdjustClose
 */
import './index.less';

import classNames from 'classnames';
import { FC, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Alert from '@/components/Alert';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import { Message } from '@/components/Message';
import WrappedButton from '@/components/WrappedButton';
import { MANAGE_SIDE, TRADE_TYPE } from '@/constants/trade';

import { Tooltip } from '@/components/ToolTip';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useIsIpBlocked } from '@/features/global/hooks';
import { useCurrentPairByDevice, useDisplayPairList, useDormantPair } from '@/features/pair/hook';
import {
  resetAdjustMarginForm,
  resetFormByChainId,
  setAdjustMarginMethod,
  switchToMarketTab,
} from '@/features/trade/actions';
import { useTradeType } from '@/features/trade/hooks';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useMarginAdjust from '@/hooks/trade/MarginAdjust/useMarginAdjust';
import useMarginAdjustByLeverage from '@/hooks/trade/MarginAdjust/useMarginAdjustByLeverage';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { useSDK } from '@/features/web3/hook';
import { MarginAdjustMethod } from '@/types/trade';
import TradeAdjustDetail from './TradeAdjustDetail';
import MarginAdjustByAmount from './TradeMarginAdjustAmount';

interface IPropTypes {
  onClose?: () => void;
}
const TradeAdjust: FC<IPropTypes> = function ({ onClose }) {
  const chainId = useChainId();
  const sdk = useSDK(chainId);
  const { setShowTradeAreaOverlay, closePositionPairSymbol } = useTradeStore();

  const tradeType = useTradeType(chainId);
  const userAddr = useUserAddr();

  // const { activeMarginAdjustMethod, toggleActiveMarginAdjustMethod, tradeAdjustMethodItems } = useMarginAdjustMethod();
  const manageSides = useAppSelector((state) => state.trade.adjustMarginControlState.manageSides);
  const leverageErrMsg = useAppSelector(
    (state) => chainId && state.trade.chainAdjustMarginByLeverageState[chainId]?.errMsg,
  );

  const currentPairFromUrl = useCurrentPairByDevice(chainId);
  const pairList = useDisplayPairList(chainId);
  const dormantPairList = useDormantPair(chainId);

  const currentPair = useMemo(() => {
    if (closePositionPairSymbol) {
      return [...pairList, ...dormantPairList].find((p) => p.symbol === closePositionPairSymbol);
    }
    return currentPairFromUrl;
  }, [closePositionPairSymbol, pairList, dormantPairList, currentPairFromUrl]);

  const dispatch = useAppDispatch();

  const {
    marginTokenAmount,
    isLoading,
    isDisable,
    adjustPositionMargin,
    // adjustFormStatus,
    adjustMarginSimulation,
    currentPosition,
    errMsg,
    isShowDepositInfo,
    depositInfo,
    displayBalance,
    adjustMarginFormState,
  } = useMarginAdjust(onClose, currentPair);

  const { deviceType } = useMediaQueryDevice();
  const { t } = useTranslation();
  // const manageSide = useMemo(() => manageSides[activeMarginAdjustMethod], [activeMarginAdjustMethod, manageSides]);
  const manageSide = useMemo(() => manageSides.MarginAdjustMethod_Amount, [manageSides]);
  // const { disable } = useMarginAdjustByLeverageInput();

  const inDisabledAdjustWithdraw = useMemo(
    () => manageSide === MANAGE_SIDE.OUT && !!currentPair?.symbol && !currentPair?.withinDeviationLimit,
    [currentPair?.symbol, currentPair?.withinDeviationLimit, manageSide],
  );

  const ipBlocked = useIsIpBlocked();

  const { debouncedInputAmountStr } = useMarginAdjustByLeverage(currentPair);

  useEffect(() => {
    dispatch(setAdjustMarginMethod(MarginAdjustMethod.Leverage));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (currentPair?.id && chainId) {
        if (tradeType === TRADE_TYPE.ADJUST_MARGIN) {
          return;
        }
        dispatch(resetAdjustMarginForm({ chainId }));
        dispatch(resetFormByChainId({ chainId }));
        dispatch(switchToMarketTab({ chainId }));
        setShowTradeAreaOverlay(undefined);
      }
    };
    // need to reset the form when pair changes
  }, [currentPair?.id, userAddr]);

  // reset state after unmount
  useEffect(() => {
    return () => {
      if (chainId) {
        if (tradeType === TRADE_TYPE.ADJUST_MARGIN) {
          return;
        }
        dispatch(resetAdjustMarginForm({ chainId }));
        dispatch(resetFormByChainId({ chainId }));
        dispatch(switchToMarketTab({ chainId }));
        setShowTradeAreaOverlay(undefined);
      }
    };
  }, []);

  return (
    <div className={classNames('syn-trade-adjust', deviceType)}>
      <div className={classNames('syn-trade-adjust-content')}>
        {/* <div className="syn-trade-adjust-method-tabs">
          {t('common.by')}
          <div style={{ width: 160 }}>
            <SynTab
              variant="rounded"
              disabled={adjustFormStatus === FETCHING_STATUS.FETCHING}
              activeKey={activeMarginAdjustMethod}
              items={tradeAdjustMethodItems}
              onChange={toggleActiveMarginAdjustMethod}
            />
          </div>
        </div> */}

        {/* {activeMarginAdjustMethod === MarginAdjustMethod.Leverage && (
          <>{currentPair && <TradeMarginAdjustLeverage pair={currentPair} />}</>
        )} */}
        {/* {activeMarginAdjustMethod === MarginAdjustMethod.Amount && (
          <>
            <MarginAdjustByAmount />
          </>
        )} */}
        <MarginAdjustByAmount currentPair={currentPair} />
        <Tooltip title={inDisabledAdjustWithdraw && t('tooltip.tradePage.disableAdjustWithdraw')}>
          <WrappedButton
            noNeedApprove={manageSide === MANAGE_SIDE.OUT}
            className={classNames('syn-trade-adjust-btn')}
            amount={marginTokenAmount}
            spenderAddress={sdk?.perp?.contracts?.gate?.address}
            loading={isLoading}
            block
            disabled={
              isDisable ||
              inDisabledAdjustWithdraw ||
              // (disable && activeMarginAdjustMethod === MarginAdjustMethod.Leverage) ||
              // (!!leverageErrMsg && activeMarginAdjustMethod === MarginAdjustMethod.Leverage) ||

              adjustMarginSimulation?.data === undefined ||
              ipBlocked
            }
            marginToken={currentPair?.rootInstrument.marginToken}
            afterApproved={adjustPositionMargin}>
            {t('common.confirm')}
          </WrappedButton>
        </Tooltip>

        {currentPair && debouncedInputAmountStr && (
          <div className="syn-trade-adjust-leverage-amount">
            <span className="syn-trade-adjust-leverage-amount-title">
              {manageSide === MANAGE_SIDE.IN ? t('common.marginRequired') : t('common.marginReleased')}
            </span>
            <div className="syn-trade-adjust-leverage-amount-spliter" />
            <span className="syn-trade-adjust-leverage-amount-qoute">
              {WrappedBigNumber.from(debouncedInputAmountStr).formatNumberWithTooltip({
                isShowTBMK: true,
                suffix: currentPair.rootInstrument.marginToken.symbol,
              })}
            </span>
          </div>
        )}

        {errMsg && (
          <>
            <Message msg={errMsg} status="warning" />
          </>
        )}
        {leverageErrMsg && (
          <>
            <Alert message={leverageErrMsg} type="error" showIcon />
          </>
        )}
        {isShowDepositInfo && depositInfo}
        {/* {!isShowDepositInfo && isShowVaultChange && vaultChangeInfo} */}
        {displayBalance.mul(0.95).lte(adjustMarginFormState.amount) && manageSide === MANAGE_SIDE.OUT && (
          <Alert
            message={
              <Trans t={t} i18nKey="common.tradePage.adjustMargin.maxWithdrawPolicy" components={{ b: <b /> }} />
            }
            type="info"
            showIcon
          />
        )}
        {adjustMarginSimulation && (
          <TradeAdjustDetail simulation={adjustMarginSimulation} currentPosition={currentPosition} />
        )}
      </div>
    </div>
  );
};

export default TradeAdjust;
